import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStoreValue, storeSet } from '../../store';
import TimelineEditor from './TimelineEditor';

/**
 * MyStudioTimeline - MyStudio-specific wrapper for TimelineEditor
 *
 * Provides:
 * - Store integration for timeline state and volumes
 * - Fixed layers (MUSIC, SFX, MIX)
 * - Layer-specific volume mixing
 * - Mute state management
 * - MyStudio-specific layer colors
 */
const MyStudioTimeline = ({
    mode = 'sb_editor',
    videoUrl = null,
    videoPoster = null,
    tracks: initialTracks = [],
    layers: propLayers,
    layersData = null,
    onTracksChange,
    onCut,
    onVideoUpload,
    onExternalDrop,
    onLayerSelectionComplete,
    onAiNoteSelectionComplete,
    aiNoteSubmissions = [],
    activeAiNoteHighlight = null,
    onAiNoteHighlight,
    undo,
    redo,
    canUndo = false,
    canRedo = false,
    undoLabel = null,
    redoLabel = null,
}) => {
    // Store integration
    const timelineState = useStoreValue('timelineState');
    const timelineVolume = useStoreValue('timelineVolume');
    const trackMusicVolume = useStoreValue('trackMusicVolume');
    const trackSfxVolume = useStoreValue('trackSfxVolume');
    const trackDialogueVolume = useStoreValue('trackDialogueVolume');
    const trackMusicMuted = useStoreValue('trackMusicMuted');
    const trackSfxMuted = useStoreValue('trackSfxMuted');
    const trackDialogueMuted = useStoreValue('trackDialogueMuted');
    const timelineEditingMode = useStoreValue('timelineEditingMode');
    const timelineRefMode = useStoreValue('timelineRefMode');
    const penaiViewData = useStoreValue('penaiViewData');

    // Use prop layers if provided, otherwise use mode-specific logic
    const layers = propLayers || (mode === 'sb_editor'
        ? (timelineRefMode ? ['REFERENCE', 'SFX', 'MIX'] : ['MUSIC', 'SFX', 'MIX'])
        : undefined); // Let core handle dynamic layers in free mode

    // Volume calculation (MyStudio-specific logic)
    const getTrackVolume = useCallback((track) => {
        const layer = track.layer;
        let layerVolume = 100;
        let isMuted = false;

        // Get layer-specific volume and mute state
        if (mode === 'sb_editor') {
            switch (layer) {
                case 'MUSIC':
                    layerVolume = trackMusicVolume;
                    isMuted = trackMusicMuted;
                    break;
                case 'SFX':
                    layerVolume = trackSfxVolume;
                    isMuted = trackSfxMuted;
                    break;
                case 'MIX':
                    layerVolume = trackDialogueVolume;
                    isMuted = trackDialogueMuted;
                    break;
                case 'REFERENCE':
                    // REFERENCE shares the MUSIC channel volume knob when active
                    layerVolume = trackMusicVolume;
                    isMuted = trackMusicMuted;
                    break;
                default:
                    layerVolume = 100;
                    isMuted = false;
            }
        }

        // If muted or volume is 0, return 0
        if (isMuted || layerVolume === 0 || timelineVolume === 0) {
            return 0;
        }

        // Calculate effective volume: general × layer
        return (timelineVolume / 100) * (layerVolume / 100);
    }, [mode, timelineVolume, trackMusicVolume, trackSfxVolume, trackDialogueVolume,
        trackMusicMuted, trackSfxMuted, trackDialogueMuted]);

    // Layer colors (MyStudio-specific)
    const getLayerColors = useCallback((layerId) => {
        if (mode === 'sb_editor') {
            // Use layersData prop for three fixed colors
            return (layersData && layersData[layerId]) || { color: '#A069E8', bgColor: 'rgba(160, 105, 232, 0.3)' };
        }
        // Free mode: use default color
        return { color: '#BCE5FF', bgColor: 'rgba(188, 229, 255, 0.60)' };
    }, [mode, layersData]);

    // Internal state for time control
    const [controlledTime, setControlledTime] = useState(null);
    const timelineRef = useRef({ duration: 0 });

    // Playback state
    const isPlaying = timelineState === 'playing';

    const handlePlaybackChange = useCallback((playing) => {
        storeSet('timelineState', playing ? 'playing' : 'paused');
    }, []);

    const handleTimeChange = useCallback((time) => {
        // Track the current time
        timelineRef.current.time = time;
    }, []);

    const handleDurationChange = useCallback((duration) => {
        timelineRef.current.duration = duration;
    }, []);

    // Watch for external timeline state changes (stopped, backward, forward)
    useEffect(() => {
        if (timelineState === 'stopped') {
            // Stop and reset to beginning
            setControlledTime(0);
            storeSet('timelineState', 'paused');
        } else if (timelineState === 'backward') {
            // Jump to beginning
            setControlledTime(0);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        } else if (timelineState === 'forward') {
            // Jump to end
            setControlledTime(timelineRef.current.duration || 999999);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        }
    }, [timelineState, isPlaying]);

    // Clear controlled time after it's been applied
    useEffect(() => {
        if (controlledTime !== null) {
            const timer = setTimeout(() => setControlledTime(null), 100);
            return () => clearTimeout(timer);
        }
    }, [controlledTime]);

    // Pause playback when MUSIC/REFERENCE toggle changes to avoid ghost audio mid-switch
    useEffect(() => {
        if (isPlaying) {
            storeSet('timelineState', 'paused');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timelineRefMode]); // intentionally only triggers on toggle change

    return (
        <TimelineEditor
            mode={mode}
            videoUrl={videoUrl}
            videoPoster={videoPoster}
            tracks={initialTracks}
            layers={layers}
            getTrackVolume={getTrackVolume}
            layerColors={getLayerColors}
            isPlayingExternal={isPlaying}
            currentTimeExternal={controlledTime}
            timelineEditingMode={timelineEditingMode}
            onPlaybackChange={handlePlaybackChange}
            onTimeChange={handleTimeChange}
            onDurationChange={handleDurationChange}
            onTracksChange={onTracksChange}
            onCut={onCut}
            onVideoUpload={onVideoUpload}
            onExternalDrop={onExternalDrop}
            onLayerSelectionComplete={onLayerSelectionComplete}
            onAiNoteSelectionComplete={onAiNoteSelectionComplete}
            aiNoteSubmissions={aiNoteSubmissions}
            activeAiNoteHighlight={activeAiNoteHighlight}
            onAiNoteHighlight={onAiNoteHighlight}
            penaiViewData={penaiViewData}
            undo={undo}
            redo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            undoLabel={undoLabel}
            redoLabel={redoLabel}
        />
    );
};

export default MyStudioTimeline;
